import { Fetcher } from '@this/src/util';
import type { FeedbackType } from '../types/messages';
import type { ProposalBundle, ProposalBundleState, SummaryItemCategory } from '../types/proposalBundles';
import { mapProposalBundlesResponse, type FetchProposalBundlesResponse } from './proposalBundlesResponse';

export const fetchProposalBundles = async (tripId: string): Promise<ProposalBundle[]> => {
  const response = await Fetcher.get<FetchProposalBundlesResponse>(`/trips/${tripId}/ai_proposal_bundles.json`);
  return mapProposalBundlesResponse(response);
};

type RejectProposalBundleResponse = {
  success: boolean;
  bundle_id: number;
  state: string;
};

type UpdateProposalBundleFeedbackResponseArgs = {
  id: number;
  feedback: FeedbackType;
  feedback_comment_submitted: boolean;
};

export const updateProposalBundleFeedback = async (
  tripId: string,
  bundleId: number,
  feedbackType: FeedbackType,
  feedbackComment?: string
): Promise<{ id: number; feedback: FeedbackType; feedbackCommentSubmitted: boolean }> => {
  const response = await Fetcher.patch<UpdateProposalBundleFeedbackResponseArgs>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/feedback.json`,
    {
      feedback_type: feedbackType,
      feedback_comment: feedbackComment
    }
  );
  return {
    id: response.id,
    feedback: response.feedback,
    feedbackCommentSubmitted: response.feedback_comment_submitted
  };
};

export const rejectProposalBundle = async (tripId: string, bundleId: number): Promise<{ success: boolean }> => {
  const response = await Fetcher.patch<RejectProposalBundleResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/reject.json`
  );
  return { success: response.success };
};

type SelectProposalItemResponse = {
  success: boolean;
  bundle_id: number;
  item_id: number;
  state: ProposalBundleState;
};

export const selectProposalItem = async (
  tripId: string,
  bundleId: number,
  itemId: number
): Promise<{ success: boolean; state: ProposalBundleState }> => {
  const response = await Fetcher.patch<SelectProposalItemResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/items/${itemId}/select.json`
  );
  return { success: response.success, state: response.state };
};

type SummaryItemResponse = {
  category: SummaryItemCategory;
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

type SummaryResponse = {
  items: SummaryItemResponse[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

type CompleteProposalSelectionResponse = {
  success: boolean;
  message_id: number | null;
  summary: SummaryResponse | null;
  has_selection: boolean;
  already_completed: boolean;
};

export const completeProposalSelection = async (
  tripId: string
): Promise<{ success: boolean; alreadyCompleted: boolean }> => {
  const response = await Fetcher.post<CompleteProposalSelectionResponse>(
    `/trips/${tripId}/ai_proposal_bundles/complete.json`,
    {}
  );
  return { success: response.success, alreadyCompleted: response.already_completed };
};
